import React, { useMemo, useState } from 'react';
import { TableColumn, Tag } from 'tea-component';
import {
  influencerCoreTagsTipsMap,
  keyMetricsMap,
  tableColumnsMap,
  // tagsOptionsMap,
} from '../constants';
import BubbleCommon, {
  influencerTableTagsTipsText,
} from '@/pages/opinion/components/BubbleCommon';
import { arrayToObj, transRegion } from '@/pages/opinion/utils';
import HoverSortCommon from '@/pages/opinion/components/HoverSortCommon';
import { getLocale, useIntl } from 'umi';
import { EDrilldownMode, useDrilldownParams } from '@op/hooks/useDrilldownParams';
import SvgIcon from '@home/components/SvgIcon';
import { NO_DATE } from '@op/constants';
import TableMultipleSelectFilter from '@op/components/TableMultipleSelectFilter';
import {
  ChartTableElement,
  getInfluencerElement,
  ValueTableElement,
} from './components/TableColumns';
import { InfluencerExtraTags } from '../components/InfluenceExtraTags';
import EllipsisBubble from '@op/components/EllipsisBubble';
import CommonBubble from '@home/components/CommonBubble';
import CurrentGameIcon from '../BrandCreatorList/components/RelatedGame';
import { useRequestFontWebConfig } from '@op/hooks/useFrontWebConfig';
// 指标列宽度
const columnsWidth = '200px';

export default function useTableColumns({
  linkTime,
  mediaType,
  selectKeys,
  selectedKeysMap,
  sort,
  changeSortInfo,
  progressVisible,
  params,
  isAuto,
  tags,
  setTags,
  tagList,
  chartMaxValue,
  gameInfo,
  jumpType,
}: any) {
  const { formatMessage } = useIntl();
  const isEn = getLocale() === 'en-US';
  const { goPage } = useDrilldownParams({ jumpType });

  const [displayShowAllTagsButton, setDisplayShowAllTagsButton] =
    useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const jumpToFeeds = (userId: string) => {
    // 通转feeds页面，暂时不跳
    goPage({
      path: `/opinion/Feeds/Feeds${gameInfo?.edition_unified_id
        ? `?gameid=${gameInfo?.edition_unified_id}&tab=socialMedia`
        : '?tab=socialMedia'
        }`,
      filter: {
        statsDetailType: 'feeds-Influencer',
        anchor_md5s: [userId],
        exclude_account_and_keyword: true,
        ...params,
      },
      // Feeds 为特殊页面，按原方式写入本地 sessionStorage
      mode: EDrilldownMode.Local,
    });
  };

  const tagsOptionsMap = useMemo(() => arrayToObj(tagList, 'value'), [tagList]);
  // 表格的Columns 和 排序的key
  const [tableColumns, sortKeys]: [TableColumn<any>[], Array<string>] =
    useMemo(() => {
      const influencerTips = keyMetricsMap[mediaType]?.kolNumber?.tips;
      const options = tagList?.filter((item) => !item?.no_filter);
      const resultList: TableColumn<any>[] = [
        getInfluencerElement({
          formatMessage,
          isAuto,
          jumpToFeeds,
          transRegion,
          influencerTips,
        }),
        {
          key: 'tags',
          width: 310,
          header: (
            <div className="dbo-influencer-table-tags-header">
              <TableMultipleSelectFilter
                options={options}
                value={tags}
                onChange={setTags}
                listWidth={'auto'}
                listHeight={185}
                needFormatMessage={false}
                button={(icon) => (
                  <div className={`tag-filter-box ${tags?.length !== tagList?.length && 'active'}`} >
                    <SvgIcon type="new-logo"></SvgIcon>
                    {formatMessage({ id: 'Tags' })}
                    {icon}
                    <BubbleCommon
                      placement="top"
                      tipsMap={influencerTableTagsTipsText}
                    ></BubbleCommon>
                  </div>
                )}
              />
              {displayShowAllTagsButton && (
                <span
                  className="button-text"
                  onClick={() => setShowAllTags(!showAllTags)}
                >
                  {showAllTags
                    ? formatMessage({ id: 'Collapse' })
                    : formatMessage({ id: 'Show all tag' })}
                </span>
              )}
            </div>
          ),
          render: ({ tags, ext_tags }) => {
            if (
              (!Array.isArray(tags) || tags.length === 0) &&
              (!Array.isArray(ext_tags) || ext_tags.length === 0)
            )
              return '-';
            const tagsView =
              tags
                ?.map((item: string, index: number) => {
                  const tagName = item.replace('is_', '').replaceAll('_', '-');
                  const itemInfo = tagsOptionsMap?.[tagName];
                  if (!itemInfo) return null;
                  const { color, text, background, value } = itemInfo;
                  return {
                    id: value,
                    element: (
                      <BubbleCommon
                        tipsMap={influencerCoreTagsTipsMap?.[value]}
                        showIcon={false}
                        placement="top"
                        childClassName={'tag-item'}
                        key={`${value}#${index}`}
                      >
                        <Tag
                          className="tag-item"
                          style={{ color, backgroundColor: background }}
                          key={index}
                        >
                          {text || formatMessage({ id: NO_DATE.EMPTY })}
                        </Tag>
                      </BubbleCommon>
                    ),
                  };
                })
                .filter((val: React.ReactNode) => val !== null) || [];

            const extTagsView =
              ext_tags
                ?.map((item: { name: string; tags: any }) => {
                  const { name, tags } = item;
                  const itemInfo = tagsOptionsMap?.[name];
                  if (!itemInfo) return null;
                  const { color, text, background, value } = itemInfo;

                  return {
                    id: value,
                    element: (
                      <InfluencerExtraTags
                        tags={tags}
                        name={name}
                        key={value}
                        color={color}
                        backgroundColor={background}
                        text={text}
                      />
                    ),
                  };
                })
                .filter((val: React.ReactNode) => val !== null) || [];

            // 避免未加入的新tags 渲染空白
            const allTagsViews =
              tagsView.length || extTagsView.length
                ? [...tagsView, ...extTagsView]
                : '-';
            if (allTagsViews === '-') return <>{allTagsViews}</>;
            if (allTagsViews.length > 4 && !displayShowAllTagsButton) {
              setDisplayShowAllTagsButton(true);
            }
            if (!showAllTags) {
              return (
                <>
                  {allTagsViews.slice(0, 4).map((val) => val.element)}
                  {allTagsViews.length > 4 && (
                    <CommonBubble
                      content={
                        <>
                          {allTagsViews.slice(4).map((val) => {
                            const { id } = val;
                            const itemInfo = tagsOptionsMap?.[id] || {};
                            return (
                              <div key={itemInfo.value}>
                                {itemInfo?.text ||
                                  formatMessage({ id: NO_DATE.EMPTY })}
                              </div>
                            );
                          })}
                        </>
                      }
                    >
                      <Tag
                        className="tag-item"
                        style={{
                          color: 'rgb(106, 109, 126)',
                          backgroundColor: 'rgb(235, 236, 241)',
                        }}
                      >
                        +{allTagsViews.length - 4}
                      </Tag>
                    </CommonBubble>
                  )}
                </>
              );
            }
            return <>{allTagsViews?.map((val) => val.element)}</>;

            return (
              <EllipsisBubble
                bubbleContent={
                  <>
                    {allTagsViews.map((val) => {
                      const { key } = val;
                      const itemInfo = tagsOptionsMap?.[key];
                      return (
                        <div key={itemInfo.value}>
                          {formatMessage({
                            id: itemInfo.text || NO_DATE.EMPTY,
                          })}
                        </div>
                      );
                    })}
                  </>
                }
              >
                {allTagsViews}
              </EllipsisBubble>
            );
          },
        },
      ];
      const sortKeys: Array<string> = [];
      const otherColumns = tableColumnsMap[mediaType] || [];
      otherColumns.forEach(
        ({
          key,
          header,
          tips,
          width,
          showChart,
          align = 'right',
          showRatio,
          showRelatedGame,
        }: any) => {
          let headerInfo = (
            <div className="dbo-influencer-table-header-box">
              <BubbleCommon placement="top" tipsMap={tips} showIcon={false}>
                <div className="dbo-influencer-table-header">
                  {formatMessage({ id: header })}
                </div>
              </BubbleCommon>
              {showRelatedGame && (
                <CurrentGameIcon
                  gameInfo={gameInfo}
                  beforeIcon={
                    isEn && (
                      <span className="before-icon-text">
                        {formatMessage({ id: 'Related to the Game' })}
                      </span>
                    )
                  }
                  afterIcon={
                    !isEn && (
                      <span className="after-icon-text">
                        {formatMessage({ id: 'Related to the Game' })}
                      </span>
                    )
                  }
                />
              )}
            </div>
          );
          if (key !== 'followers_grow') {
            sortKeys.push(key);
          } else {
            headerInfo = (
              <HoverSortCommon
                className="tea-text-rightfollowers-grow"
                sort={sort}
                sortOptions={[
                  {
                    text: formatMessage({
                      id: 'Sort influencers with more than 5K followers (faster)',
                    }),
                    value: 'followers_grow_more_5k',
                  },
                  {
                    text: formatMessage({
                      id: 'Sort all influencers (time-consuming)',
                    }),
                    value: 'followers_grow',
                  },
                ]}
                sortChange={(sort: { by: string; order: 'asc' | 'desc' }) =>
                  changeSortInfo(sort)
                }
              >
                {headerInfo}
              </HoverSortCommon>
            );
          }

          resultList.push({
            key,
            width: width || columnsWidth,
            header: headerInfo,
            align: align,
            render: (item: { [x: string]: any }) => {
              if (showChart) {
                return (
                  <ChartTableElement
                    valueKey={key}
                    item={item}
                    title={header}
                    linkTime={linkTime}
                    chartMaxValue={chartMaxValue}
                    showRatio={showRatio}
                  ></ChartTableElement>
                );
              }
              return (
                <ValueTableElement
                  valueKey={key}
                  item={item}
                  title={header}
                  linkTime={linkTime}
                  progressVisible={progressVisible}
                ></ValueTableElement>
              );
            },
          });
        },
      );
      return [resultList, sortKeys];
    }, [
      isAuto,
      mediaType,
      selectKeys,
      linkTime,
      sort,
      progressVisible,
      formatMessage,
      tags,
      chartMaxValue,
      displayShowAllTagsButton,
      showAllTags,
    ]);

  return {
    tableColumns,
    sortKeys,
  };
}
